import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PouchColorLegend, Pouch, Group, POUCH_COLORS, Sheet } from '../types';
import { LayerAwareCounter } from './LayerAwareCounter';
import {
  buildColorTotalLayerTooltipLines,
  resolveLayerNumericTooltipHtml,
} from '../utils/layerValueTooltip';
import { formatTooltip } from '../utils/tooltipFormatter';
import { countLegendLabel } from '../i18n';
import { useLocale } from '../contexts/LocaleContext';
import { pouchCountsAsTimeForSheetDisplay } from '../logic/GroupLogic';
import './styles/LegendsComponent.scss';

interface PouchColor {
  id: string;
  value: string;
  name: string;
}

interface LegendsComponentProps {
  pouches: Pouch[];
  groups: Group[];
  colorLegends: PouchColorLegend[];
  activeSheetId: string;
  isSheetSwitching: boolean;
  onUpdateColorLegend: (sheetId: string, colorId: string, legend: string) => void;
  calculateColorTotal: (colorId: string) => number;
  calculateColorCount: (colorId: string) => number;
  onEditConfirm?: () => void;  // 編集確定を親コンポーネントに通知するためのコールバック
  onLegendHoverChange?: (colorId: string | null) => void;  // 凡例ホバー状態の変更を通知
  currentSheet?: Sheet;
}

const getDefaultLegend = (colorId: string): string => {
  const color = POUCH_COLORS.find(c => c.id === colorId);
  return color?.name || '';
};

const formatDetailedNumber = (value: number): string => {
  const r8 = Math.round(value * 1e8) / 1e8;
  const r9 = Math.round(value * 1e9) / 1e9;
  const hasMore = Math.abs(r9 - r8) > 0;
  const intPart = Math.floor(Math.abs(r8));
  const decPart = Math.abs(r8) - intPart;
  const sign = r8 < 0 ? '-' : '';
  let result = decPart > 0
    ? `${sign}${intPart.toLocaleString()}.${decPart.toFixed(8).replace(/0+$/, '').substring(2)}`
    : `${sign}${intPart.toLocaleString()}`;
  if (hasMore) result += '...';
  return result;
};

const formatMinutesAsTime = (signedMinutes: number): string => {
  const totalMinutes = Math.round(Math.abs(signedMinutes));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sign = totalMinutes === 0 ? '' : (signedMinutes < 0 ? '-' : '');
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

const formatBodyCountNumber = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const LegendsComponent: React.FC<LegendsComponentProps> = ({
  pouches,
  groups,
  colorLegends,
  activeSheetId,
  isSheetSwitching,
  onUpdateColorLegend,
  calculateColorTotal,
  calculateColorCount,
  onEditConfirm,
  onLegendHoverChange,
  currentSheet,
}) => {
  const { appCopy, tooltipCopy } = useLocale();
  // 使用されている色が1種類かどうかをチェック
  const isSingleColor = pouches.length > 0 && 
    pouches.filter(p => p.color !== pouches[0].color).length === 0;

  // 凡例の値を管理するための内部state
  const [legendStates, setLegendStates] = useState<{[key: string]: string}>({});
  
  // 編集中の凡例を追跡
  const [editingLegendId, setEditingLegendId] = useState<string | null>(null);
  
  // IME入力中かどうかの状態
  const [isComposing, setIsComposing] = useState(false);
  
  // ホバー中の凡例の色IDを追跡
  const [hoveredColorId, setHoveredColorId] = useState<string | null>(null);
  
  // 各凡例のinput要素への参照
  const inputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});

  // 編集確定フラグ用のref（パウチコンポーネントと同様）
  const justConfirmedRef = useRef(false);
  const confirmTimeoutRef = useRef<number | null>(null);

  // 編集中のIDを追跡するためのref
  const editingLegendIdRef = useRef<string | null>(null);
  
  // editingLegendIdの変更を追跡
  useEffect(() => {
    editingLegendIdRef.current = editingLegendId;
  }, [editingLegendId]);

  // colorLegendsが変更されたら内部stateを初期化（編集中の凡例は除外）
  useEffect(() => {
    const initialStates: {[key: string]: string} = {};
    colorLegends.forEach(legend => {
      // 編集中の凡例は現在の値を保持し、それ以外はプロップの値で更新
      if (editingLegendIdRef.current === legend.colorId && legendStates[legend.colorId] !== undefined) {
        initialStates[legend.colorId] = legendStates[legend.colorId];
      } else {
        initialStates[legend.colorId] = legend.legend;
      }
    });
    
    setLegendStates(initialStates);
  }, [colorLegends]);

  // クリックイベントが伝播するのを防止する関数
  const handleLegendClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 凡例のホバーハンドラー
  const handleLegendMouseEnter = (colorId: string) => {
    setHoveredColorId(colorId);
    if (onLegendHoverChange) {
      onLegendHoverChange(colorId);
    }
  };

  const handleLegendMouseLeave = () => {
    setHoveredColorId(null);
    if (onLegendHoverChange) {
      onLegendHoverChange(null);
    }
  };

  // 入力中の値を一時保存
  const handleLegendInput = (colorId: string, value: string) => {
    setLegendStates(prev => ({
      ...prev,
      [colorId]: value
    }));
    // 編集中のIDを設定
    setEditingLegendId(colorId);
  };

  // フォーカスを失ったときに値を確定
  const handleLegendBlur = (colorId: string) => {
    // 値が変更されていれば親コンポーネントに通知
    if (legendStates[colorId] !== undefined) {
      onUpdateColorLegend(activeSheetId, colorId, legendStates[colorId]);
    }
    
    // 編集中のIDをクリア
    setEditingLegendId(null);
    
    // 確定フラグを設定してタイムアウト後にリセット（PouchComponentと同様）
    justConfirmedRef.current = true;
    
    // 前回のタイマーがあれば解除
    if (confirmTimeoutRef.current !== null) {
      window.clearTimeout(confirmTimeoutRef.current);
    }
    
    // ActiveSheetComponentの確定フラグも設定する
    if (onEditConfirm) {
      onEditConfirm();
    }
    
    // 確定フラグを一定時間後にリセット
    confirmTimeoutRef.current = window.setTimeout(() => {
      justConfirmedRef.current = false;
      confirmTimeoutRef.current = null;
    }, 300);
  };
  
  // キー入力処理
  const handleKeyDown = (e: React.KeyboardEvent, colorId: string) => {
    // IME入力中は何もしない（確定待ち）
    if (isComposing) {
      return;
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleLegendBlur(colorId);
      // フォーカスを外す
      inputRefs.current[colorId]?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      // 編集をキャンセル - colorLegendsの元の値に戻す
      const originalLegend = colorLegends.find(l => l.colorId === colorId)?.legend || '';
      setLegendStates(prev => ({
        ...prev,
        [colorId]: originalLegend
      }));
      // 編集中のIDをクリア
      setEditingLegendId(null);
      // フォーカスを外す
      inputRefs.current[colorId]?.blur();
    }
  };
  
  // マウスアップイベントハンドラ（パウチコンポーネントと同様）
  const handleMouseUp = useCallback((e: MouseEvent) => {
    // 確定直後なら新規パウチ作成を抑止
    if (justConfirmedRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);
  
  // マウスダウンイベントハンドラ - パウチタイトルと同様の処理
  const handleMouseDown = useCallback((e: MouseEvent) => {
    // 編集中の凡例があれば確定する
    if (editingLegendId) {
      // クリックされた要素が編集中の凡例の入力欄か確認
      const target = e.target as HTMLElement;
      const isClickedInput = target.tagName.toLowerCase() === 'input';
      
      // 入力欄以外のクリックで編集中なら確定
      if (!isClickedInput) {
        handleLegendBlur(editingLegendId);
        // パウチ作成を防ぐためにイベント伝播を止める
        e.stopPropagation();
      }
    }
  }, [editingLegendId]);

  // コンポーネントのマウント時にdocumentに対してイベントリスナーを設定
  useEffect(() => {
    // ドキュメント全体のクリックを検知
    document.addEventListener('mousedown', handleMouseDown);
    // マウスアップイベントリスナーを追加
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // タイマーのクリーンアップ
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
    };
  }, [handleMouseDown, handleMouseUp]);

  return (
    <div 
      className={`legends ${isSingleColor ? 'singlecolor' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
      }}
    >
      <ul>
        {colorLegends?.sort((a, b) => {
          // 0番目を最後に、それ以外は番号順にする汎用的な順序
          const getColorOrder = (colorId: string) => {
            const colorIndex = POUCH_COLORS.findIndex(c => c.id === colorId);
            if (colorIndex === 0) return POUCH_COLORS.length; // 0番目を最後に
            return colorIndex >= 0 ? colorIndex : 0; // 1,2,3,4,5...の順
          };
          
          return getColorOrder(a.colorId) - getColorOrder(b.colorId);
        }).map(legend => {
          const color = POUCH_COLORS.find(c => c.id === legend.colorId);
          // この色のパウチが1つでも存在するかチェック
          const colorExists = pouches.some(p => p.color === legend.colorId);
          const colorTotal = calculateColorTotal(legend.colorId);
          const colorMembers = pouches.filter(
            p => p.color === legend.colorId && !p._isDeleting && !p._isGtpRenderPreview
          );
          const allColorMembersAreTime =
            colorMembers.length > 0 &&
            colorMembers.every((p) =>
              pouchCountsAsTimeForSheetDisplay(
                p,
                pouches,
                groups,
                currentSheet?.balancingPouchId
              )
            );
          const legendTotalPlainVisible = allColorMembersAreTime
            ? `${formatDetailedNumber(colorTotal)} (${formatMinutesAsTime(colorTotal)})`
            : formatDetailedNumber(colorTotal);
          const legendLayerLines = currentSheet?.layers?.length
            ? buildColorTotalLayerTooltipLines(currentSheet, legend.colorId, pouches, groups)
            : [legendTotalPlainVisible];
          const legendTotalTooltipHtml = resolveLayerNumericTooltipHtml(
            currentSheet,
            legendTotalPlainVisible,
            legendLayerLines
          );
          const legendCompositeTooltipHtml = [
            legendTotalTooltipHtml,
            `<b>${countLegendLabel(formatBodyCountNumber(calculateColorCount(legend.colorId)))}</b>`,
            formatTooltip(`Click: ${tooltipCopy.editColorLabel}`),
          ].filter(Boolean).join('<br/>');
          
          if (color) {
            // 常に内部stateの値を表示する
            const displayValue = legendStates[legend.colorId] !== undefined
              ? legendStates[legend.colorId]
              : legend.legend;

            return (
              <li
                key={legend.colorId} 
                className={colorExists ? '' : 'empty'}
                data-tooltip-id="tt" data-tooltip-html={legendCompositeTooltipHtml}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseEnter={() => handleLegendMouseEnter(legend.colorId)}
                onMouseLeave={handleLegendMouseLeave}
                style={{ '--legend-color': color.value } as React.CSSProperties}
              >
                <div className="legend-color"></div>
                <div className="legend-info">
                  <input 
                    ref={(el) => {
                      inputRefs.current[legend.colorId] = el;
                      return undefined;
                    }}
                    type="text" 
                    value={displayValue}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      handleLegendInput(legend.colorId, target.value);
                    }}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    onKeyDown={(e) => handleKeyDown(e, legend.colorId)}
                    onBlur={() => handleLegendBlur(legend.colorId)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const target = e.currentTarget;
                      const wasFocused = document.activeElement === target;
                      if (!wasFocused) {
                        // 初回クリックの場合、フォーカス後に全選択
                        requestAnimationFrame(() => {
                          target.select();
                          setEditingLegendId(legend.colorId);
                        });
                      }
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      const target = e.target as HTMLInputElement;
                      const wasFocused = document.activeElement === target;
                      if (!wasFocused) {
                        requestAnimationFrame(() => {
                          target.focus();
                          target.select();
                          setEditingLegendId(legend.colorId);
                        });
                      } else {
                        target.focus();
                        setEditingLegendId(legend.colorId);
                      }
                    }}
                    placeholder={colorExists ? (() => {
                      const existingLegends = colorLegends.filter(l => pouches.some(p => p.color === l.colorId));
                      const titledLegends = existingLegends.filter(l => l.legend.trim() !== '');
                      const untitledLegends = existingLegends.filter(l => l.legend.trim() === '');
                      
                      // タイトルなしが1つだけの場合、「misc.」を返す
                      if (untitledLegends.length === 1 && untitledLegends[0].colorId === legend.colorId) {
                        return appCopy.table.misc;
                      }
                      
                      // それ以外は従来通りA、B、C...
                      return String.fromCharCode(65 + existingLegends.findIndex(l => l.colorId === legend.colorId));
                    })() : 
                      getDefaultLegend(legend.colorId)
                    }
                  />
                  <div className='legend-total'>
                    <LayerAwareCounter
                      sheet={currentSheet}
                      value={Math.abs(colorTotal)}
                      isNegative={colorTotal < 0}
                      animateFromZero={isSheetSwitching}
                      groupId={`color-${legend.colorId}`}
                      duration={200}
                      suppressTooltip
                      displayAsTime={allColorMembersAreTime}
                      timeDisplayTag="strong"
                    />
                  </div>
                </div>
              </li>
            );
          }
          return null;
        })}
      </ul>
    </div>
  );
};

// React.memoで再レンダリングを最適化
export default React.memo(LegendsComponent, (prevProps, nextProps) => {
  return (
    prevProps.pouches.length === nextProps.pouches.length &&
    prevProps.pouches.every((prevPouch, index) => {
      const nextPouch = nextProps.pouches[index];
      return nextPouch && 
        prevPouch.id === nextPouch.id &&
        prevPouch.value === nextPouch.value &&
        prevPouch.multiplier === nextPouch.multiplier &&
        prevPouch.color === nextPouch.color;
    }) &&
    prevProps.colorLegends.length === nextProps.colorLegends.length &&
    prevProps.colorLegends.every((prevLegend, index) => {
      const nextLegend = nextProps.colorLegends[index];
      return nextLegend && 
        prevLegend.colorId === nextLegend.colorId &&
        prevLegend.legend === nextLegend.legend;
    }) &&
    prevProps.activeSheetId === nextProps.activeSheetId &&
    prevProps.isSheetSwitching === nextProps.isSheetSwitching &&
    prevProps.currentSheet?.activeLayerId === nextProps.currentSheet?.activeLayerId &&
    (prevProps.currentSheet?.layers?.length ?? 0) === (nextProps.currentSheet?.layers?.length ?? 0)
  );
}); 